import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { io } from "socket.io-client";
import { GlobalStyles } from "@mui/material";
import { getSocketServerUrl } from "../utils/socketUrl";
import {
  Grid,
  Card,
  Typography,
  Box,
  CircularProgress,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
} from "recharts";
import {
  ShoppingCart as OrdersIcon,
  People as CustomersIcon,
  Inventory as ProductsIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Pending as PendingIcon,
  LocalMall as PackagingIcon,
  Cancel as CanceledIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

const resolveImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  return `${API_BASE_URL}${path}`;
};

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const fetchingRef = React.useRef(false);
  const [orderTimeRange] = useState("thisYear");
  const [earningTimeRange] = useState("thisYear");
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [liveEvents, setLiveEvents] = useState([]);
  const [salesTrend, setSalesTrend] = useState([]);
  const [salesLoading, setSalesLoading] = useState(true);
  const [salesTrendRange, setSalesTrendRange] = useState("30days");
  const navigate = useNavigate();

  const handleViewOrder = (orderId) => {
    navigate(`/dashboard/orders/${orderId}`);
  };

  // Rate limiting: only allow API calls every 30 seconds
  const RATE_LIMIT_MS = 30000;

  // Manual refresh function that bypasses rate limiting
  const handleManualRefresh = () => {
    console.log("🔄 Manual refresh triggered");
    setLastFetchTime(0); // Reset rate limit
    setFetching(false); // Reset fetching state
    setLoading(false); // Reset loading state
    setTimeout(() => fetchDashboardData(), 100); // Small delay to ensure state updates
  };

  const fetchDashboardData = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (fetchingRef.current) {
      console.log("⏳ Already fetching dashboard data, skipping...");
      return;
    }

    const now = Date.now();
    // Rate limiting check
    if (now - lastFetchTime < RATE_LIMIT_MS) {
      console.log(
        `⏳ Rate limited. Next call allowed in ${Math.ceil((RATE_LIMIT_MS - (now - lastFetchTime)) / 1000)}s`,
      );
      return;
    }

    setLastFetchTime(now);
    fetchingRef.current = true;
    setFetching(true);
    setLoading(true);
    const startTime = Date.now();

    try {
      const adminToken =
        localStorage.getItem("adminToken") || localStorage.getItem("token");

      if (!adminToken) {
        console.error("❌ No admin token found");
        setFetching(false);
        setLoading(false);
        return;
      }

      console.log("🔍 Fetching dashboard data...");
      console.log("🔍 API URL:", `${API_BASE_URL}/analytics/dashboard`);
      console.log("🔍 Params:", { orderTimeRange, earningTimeRange });

      // Try authenticated endpoint first, fallback to public if unauthorized
      let response;
      try {
        response = await Promise.race([
          axios.get(`${API_BASE_URL}/analytics/dashboard`, {
            headers: { Authorization: `Bearer ${adminToken}` },
            params: { orderTimeRange, earningTimeRange },
            timeout: 5000, // Reduced to 5 seconds for faster feedback
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Request timeout")), 5000),
          ),
        ]);
      } catch (authError) {
        if (
          authError.response?.status === 401 ||
          authError.response?.status === 403
        ) {
          console.log("🔄 Auth failed, trying public endpoint...");
          response = await Promise.race([
            axios.get(`${API_BASE_URL}/analytics/dashboard/public`, {
              params: { orderTimeRange, earningTimeRange },
              timeout: 5000,
            }),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Request timeout")), 5000),
            ),
          ]);
        } else {
          throw authError;
        }
      }

      const endTime = Date.now();
      console.log(`✅ Dashboard data loaded in ${endTime - startTime}ms`);
      console.log("🔍 Dashboard response:", response.data);
      setStats(response.data);
    } catch (error) {
      const endTime = Date.now();
      console.error(
        `❌ Error fetching dashboard data after ${endTime - startTime}ms:`,
        error,
      );

      // Reset rate limit on error to allow immediate retry
      setLastFetchTime(0);

      // Set minimal default values when API fails
      console.log("🔄 Setting default values due to API error");
      setStats({
        totalOrders: 0,
        ordersThisMonth: 0,
        ordersLastMonth: 0,
        totalRevenue: 0,
        revenueThisRange: 0,
        revenueLastMonth: 0,
        totalCustomers: 0,
        customersThisMonth: 0,
        totalProducts: 0,
        lowStockCount: 0,
        pendingOrders: 0,
        processingOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0,
        orderBreakdown: {
          pending: 0,
          confirmed: 0,
          packaging: 0,
          delivered: 0,
          canceled: 0,
          returned: 0,
          outForDelivery: 0,
          failedToDeliver: 0,
        },
        userOverview: {
          totalCustomers: 0,
          totalUsers: 0,
        },
        topCustomers: [],
        popularProducts: [],
        topSellingProducts: [],
      });
    } finally {
      fetchingRef.current = false;
      setFetching(false);
      setLoading(false);
    }

    // Add safety timeout to prevent infinite loading
    setTimeout(() => {
      setLoading(false);
    }, 8000);
  }, [orderTimeRange, earningTimeRange]);

  const fetchSalesTrend = useCallback(async () => {
    setSalesLoading(true);

    try {
      const adminToken =
        localStorage.getItem("adminToken") || localStorage.getItem("token");
      if (!adminToken) {
        setSalesLoading(false);
        return;
      }

      console.log("🔥 SALES TREND RANGE BEING SENT:", salesTrendRange);

      const response = await axios.get(`${API_BASE_URL}/analytics/sales-chart`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        params: { range: salesTrendRange },
        timeout: 5000,
      });

      const rawData = response.data?.data || [];
      const formatted = rawData.map((item) => ({
        date: salesTrendRange === "30days"
          ? new Date(item._id).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })
          : new Date(item._id).toLocaleDateString("en-US", {
              month: "short",
              year: "2-digit",
            }),
        revenue: item.sales || item.revenue || 0,
        orders: item.orders || 0,
      }));

      setSalesTrend(formatted);
    } catch (error) {
      console.error("Error fetching sales trend:", error);
      setSalesTrend([]);
    } finally {
      setSalesLoading(false);
    }
  }, [salesTrendRange]);

  useEffect(() => {
    fetchDashboardData();
    fetchSalesTrend();
  }, [fetchDashboardData, fetchSalesTrend]);

  useEffect(() => {
    const socket = io(getSocketServerUrl(process.env.REACT_APP_API_URL), {
      transports: ["websocket", "polling"],
      withCredentials: true,
    });

    socket.emit("admin.join", "admin-room");

    socket.on("new-order", (payload) => {
      setLiveEvents((prev) => [payload, ...prev].slice(0, 5));
    });

    socket.on("order-status-updated", (payload) => {
      setLiveEvents((prev) => [payload, ...prev].slice(0, 5));
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const orderStatusCards = [
    {
      title: "Pending",
      count: stats?.pendingOrders || 0,
      color: "#00CFE8",
      icon: <PendingIcon />,
    },
    {
      title: "Processing",
      count: stats?.processingOrders || 0,
      color: "#FF9F43",
      icon: <PackagingIcon />,
    },
    {
      title: "Delivered",
      count: stats?.completedOrders || 0,
      color: "#28C76F",
      icon: <TrendingUpIcon />,
    },
    {
      title: "Canceled",
      count: stats?.cancelledOrders || 0,
      color: "#EA5455",
      icon: <CanceledIcon />,
    },
  ];

  const revenueCards = [
    {
      title: "Total Revenue",
      amount: stats?.totalRevenue || 0,
      icon: <MoneyIcon />,
      color: "#2F66FF",
    },
    {
      title: "Revenue This Year",
      amount: stats?.revenueThisRange || 0,
      icon: <TrendingUpIcon />,
      color: "#28C76F",
    },
    {
      title: "Revenue Last Month",
      amount: stats?.revenueLastMonth || 0,
      icon: <TrendingUpIcon />,
      color: "#FF9F43",
    },
    {
      title: "Low Stock Products",
      amount: stats?.lowStockCount || 0,
      icon: <ProductsIcon />,
      color: "#EA5455",
    },
  ];

  return (
    <>
      <GlobalStyles
        styles={{
          "@keyframes spin": {
            "0%": {
              transform: "rotate(0deg)",
            },
            "100%": {
              transform: "rotate(360deg)",
            },
          },
        }}
      />
      <Box sx={{ bgcolor: "#F5F7FA", minHeight: "100vh", p: { xs: 2, sm: 3 } }}>
        {/* Welcome Section */}
        <Box mb={{ xs: 2, sm: 4 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                fontSize: { xs: "1.5rem", sm: "2rem" },
              }}
            >
              Welcome Printformee
            </Typography>
            <Tooltip title="Refresh Dashboard">
              <IconButton
                onClick={handleManualRefresh}
                disabled={loading || fetching}
                sx={{
                  bgcolor: "white",
                  boxShadow: 1,
                  "&:hover": { bgcolor: "#f8fafc" },
                  "&:disabled": { opacity: 0.5 },
                }}
              >
                <RefreshIcon
                  sx={{
                    animation: fetching ? "spin 1s linear infinite" : "none",
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            variant="h6"
            color="#64748b"
            sx={{ fontSize: { xs: "1.1rem", sm: "1.4rem" } }}
          >
            Monitor your business analytics and statistics.
          </Typography>
        </Box>

        {/* Business Analytics Section */}
        <Card
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 4 },
            borderRadius: 3,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              fontSize: { xs: "0.9rem", sm: "1rem" },
            }}
          >
            Business Analytics
          </Typography>

          {/* Top Row - 3 Big Statistic Cards */}
          <Grid container spacing={{ xs: 2, sm: 3 }} mb={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  p: { xs: 2, sm: 3 },
                  textAlign: "center",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  boxShadow: 2,
                  bgcolor: "white",
                  transition:
                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                  },
                }}
              >
                <OrdersIcon
                  sx={{ fontSize: { xs: 30, sm: 40 }, color: "#2F66FF", mb: 1 }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: "#1e293b",
                    fontSize: { xs: "1.5rem", sm: "2rem" },
                  }}
                >
                  {stats?.totalOrders || 0}
                </Typography>
                <Typography
                  variant="body2"
                  color="#64748b"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Total Orders
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  p: { xs: 2, sm: 3 },
                  textAlign: "center",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  boxShadow: 2,
                  bgcolor: "white",
                  transition:
                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                  },
                }}
              >
                <ProductsIcon
                  sx={{ fontSize: { xs: 30, sm: 40 }, color: "#FF9F43", mb: 1 }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: "#1e293b",
                    fontSize: { xs: "1.5rem", sm: "2rem" },
                  }}
                >
                  {stats?.totalProducts || 0}
                </Typography>
                <Typography
                  variant="body2"
                  color="#64748b"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Total Products
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  p: { xs: 2, sm: 3 },
                  textAlign: "center",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  boxShadow: 2,
                  bgcolor: "white",
                  transition:
                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                  },
                }}
              >
                <CustomersIcon
                  sx={{ fontSize: { xs: 30, sm: 40 }, color: "#00CFE8", mb: 1 }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: "#1e293b",
                    fontSize: { xs: "1.5rem", sm: "2rem" },
                  }}
                >
                  {stats?.totalCustomers || 0}
                </Typography>
                <Typography
                  variant="body2"
                  color="#64748b"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Total Customers
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Second Row - Order Breakdown Cards */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Order Breakdown
            </Typography>
            <Grid container spacing={3}>
              {orderStatusCards.map((status, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                      boxShadow: 2,
                      bgcolor: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      transition:
                        "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 3,
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center">
                      <Box sx={{ color: status.color, mr: 2 }}>
                        {status.icon}
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {status.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: status.color }}
                    >
                      {status.count}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Card>

        <Card
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            Revenue Summary
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(4,1fr)",
              },
              gap: 3,
            }}
          >
            {revenueCards.map((card, index) => (
              <Card
                key={index}
                sx={{
                  p: 3,
                  textAlign: "center",
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #e5e7eb",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box sx={{ color: card.color, mb: 1 }}>{card.icon}</Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: "#1e293b" }}
                >
                  ₹{card.amount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  {card.title}
                </Typography>
              </Card>
            ))}
          </Box>
        </Card>
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} lg={8}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
              >
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Sales Trend
                </Typography>
                <Box sx={{ minWidth: 120 }}>
                  <select
                    value={salesTrendRange}
                    onChange={(e) => setSalesTrendRange(e.target.value)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                      backgroundColor: "white",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    <option value="30days">Last 30 Days</option>
                    <option value="6months">Last 6 Months</option>
                    <option value="1year">Last 1 Year</option>
                  </select>
                </Box>
              </Box>
              {salesLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight={260}
                >
                  <CircularProgress />
                </Box>
              ) : salesTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart
                    data={salesTrend}
                    margin={{ top: 10, right: 20, left: -10, bottom: 0 }}
                  >
                    <CartesianGrid stroke="#f5f5f5" />
                    <XAxis dataKey="date" tick={{ fill: "#64748b" }} />
                    <YAxis tick={{ fill: "#64748b" }} />
                    <RechartsTooltip />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#2F66FF"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="orders"
                      stroke="#FF9F43"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight={260}
                >
                  <Typography variant="body2" color="text.secondary">
                    No sales trend data available yet.
                  </Typography>
                </Box>
              )}
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                height: "100%",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                Recent Orders
              </Typography>
              {stats?.recentOrders?.length ? (
                <List dense>
                  {stats.recentOrders.slice(0, 5).map((order, index) => (
                    <ListItem
                      key={order.id || index}
                      secondaryAction={
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewOrder(order.id)}
                        >
                          View
                        </Button>
                      }
                    >
                      <ListItemText
                        primary={`#${order.id?.slice(-6) || "—"} • ₹${Number(order.amount || 0).toLocaleString("en-IN")}`}
                        secondary={`${order.customer} • ${new Date(order.date).toLocaleDateString("en-IN")}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recent orders available.
                </Typography>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* NEW: Additional Dashboard Sections */}
        <Card sx={{ p: 3, mb: 4, borderRadius: 3, boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Live Admin Feed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            New orders and status changes appear here in real time.
          </Typography>
          {liveEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Waiting for live activity...
            </Typography>
          ) : (
            <List dense>
              {liveEvents.map((event, index) => (
                <ListItem key={`${event.orderId || index}-${index}`} divider>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "#1976d2" }}>
                      {event.orderNumber ? event.orderNumber.slice(-2).toUpperCase() : "NF"}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={event.message || `Order ${event.orderNumber || event.orderId}`}
                    secondary={`Status: ${event.status || "pending"} • Amount: ₹${Number(event.amount || 0).toLocaleString("en-IN")}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Card>

        {/* Top Customer Section */}
        <Card
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            Top Customers
          </Typography>
          <Grid container spacing={3}>
            {stats?.topCustomers?.map((customer, index) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    boxShadow: 2,
                    bgcolor: "white",
                    cursor: "pointer",
                    transition:
                      "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: 3,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      mx: "auto",
                      mb: 1,
                      bgcolor: "#2F66FF",
                      width: 48,
                      height: 48,
                    }}
                  >
                    {customer.avatar}
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {customer.name}
                  </Typography>
                  <Typography variant="caption" color="#64748B">
                    Orders: {customer.orders}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* Most Popular Products & Top Selling Products */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <ProductsIcon sx={{ fontSize: 24, color: "#FF9F43", mr: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Most Popular Products
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate("/dashboard/product-list")}
                  sx={{ textTransform: "none" }}
                >
                  View All
                </Button>
              </Box>
              <Grid container spacing={2}>
                {stats?.popularProducts?.slice(0, 4).map((product, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: 1,
                        bgcolor: "white",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        minHeight: 220,
                      }}
                    >
                      {product.thumbnail ? (
                        <Box
                          component="img"
                          src={resolveImageUrl(product.thumbnail)}
                          alt={product.name}
                          sx={{
                            width: 60,
                            height: 60,
                            objectFit: "cover",
                            borderRadius: 1,
                            mb: 1,
                            mx: "auto",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            bgcolor: "#F3F4F6",
                            borderRadius: 1,
                            mb: 1,
                            mx: "auto",
                          }}
                        />
                      )}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="#64748B">
                          Orders: {product.orders}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <TrendingUpIcon
                    sx={{ fontSize: 24, color: "#2F66FF", mr: 1 }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Top Selling Products
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate("/dashboard/product-list")}
                  sx={{ textTransform: "none" }}
                >
                  View All
                </Button>
              </Box>
              <Box>
                {stats?.topSellingProducts?.slice(0, 4).map((product, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "white",
                      boxShadow: 1,
                      mb: index < 3 ? 1 : 0,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 2,
                          overflow: "hidden",
                          bgcolor: "#F3F4F6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {product.thumbnail ? (
                          <Box
                            component="img"
                            src={resolveImageUrl(product.thumbnail)}
                            alt={product.name}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <Typography variant="caption" color="#94A3B8">
                            No Image
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="#64748B">
                          Sold: {product.sold}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#2563EB",
                        fontWeight: 700,
                      }}
                    >
                      {product.sold}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>
        </Grid>

      </Box>
    </>
  );
}

export default Dashboard;
